import React, { useState, useEffect, memo } from 'react';
import { Card, Space, DatePicker, Button, Spin, Skeleton, message, Segmented, Statistic, Row, Col, Progress, Tooltip as AntTooltip } from 'antd';
import { MetricCardHeader } from './Shared/MetricCardHeader';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar
} from 'recharts';
import { 
  GlobalOutlined, 
  MobileOutlined, 
  DesktopOutlined,
  AudioOutlined,
  EditOutlined,
  FormOutlined,
  ReloadOutlined,
  DownloadOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getSourceDistribution } from '../../api/api';
import type { SourceDistributionResponse } from '../../types/api-models';
import { 
  formatXAxisLabel, 
  formatTooltipLabel, 
  formatPeriodDisplay,
  getXAxisInterval,
  type AggregationType 
} from '../../utils/dateFormatters';
import { chartColors, chartConfig } from '../../utils/chartColors';

const { RangePicker } = DatePicker;

interface SourceDistributionProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const SourceDistribution: React.FC<SourceDistributionProps> = memo(({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SourceDistributionResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  useEffect(() => {
    if (!isExpanded) {
      // Collapsed view: use dashboard data (30-day snapshot)
      if (dashboardData?.source_distribution) {
        setData(dashboardData.source_distribution);
      }
    }
    // Expanded view: Don't auto-load, wait for user interaction
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let fromDate: string;
      let toDate: string;
      
      if (aggregation === 'daily') {
        // For daily, use the selected date range
        const is30DayRange = dateRange[1].diff(dateRange[0], 'days') === 30;
        
        if (is30DayRange) {
          // Use exact 30 days from now to match dashboard
          const now = dayjs();
          const thirtyDaysAgo = now.subtract(30, 'days');
          fromDate = thirtyDaysAgo.format('YYYY-MM-DD');
          toDate = now.format('YYYY-MM-DD');
        } else {
          fromDate = dateRange[0].format('YYYY-MM-DD');
          toDate = dateRange[1].format('YYYY-MM-DD');
        }
      } else {
        // For weekly/monthly, use predefined periods (no date selection)
        // Use current date as end date, let backend determine the start date
        const now = dayjs();
        toDate = now.format('YYYY-MM-DD');
        
        if (aggregation === 'weekly') {
          // For weekly, use last 52 weeks
          const fiftyTwoWeeksAgo = now.subtract(52, 'weeks');
          fromDate = fiftyTwoWeeksAgo.format('YYYY-MM-DD');
        } else {
          // For monthly, use last 12 months
          const twelveMonthsAgo = now.subtract(12, 'months');
          fromDate = twelveMonthsAgo.format('YYYY-MM-DD');
        }
      }
      
      const response = await getSourceDistribution(
        aggregation,
        fromDate,
        toDate
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching source distribution:', error);
      message.error('Failed to load source distribution');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source.toLowerCase()) {
      case 'web':
        return <DesktopOutlined />;
      case 'modal':
        return <MobileOutlined />;
      default:
        return <GlobalOutlined />;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source.toLowerCase()) {
      case 'web':
        return '#1890ff';
      case 'modal':
        return '#52c41a';
      default:
        return '#faad14';
    }
  };

  const getInputModeIcon = (mode: string) => {
    switch (mode.toLowerCase()) {
      case 'voice':
        return <AudioOutlined />;
      case 'voice_edited':
        return <EditOutlined />;
      default:
        return <FormOutlined />;
    }
  };

  const getInputModeColor = (mode: string) => {
    switch (mode.toLowerCase()) {
      case 'voice':
        return '#13c2c2';
      case 'voice_edited':
        return '#722ed1';
      default:
        return '#2f54eb';
    }
  };

  const getInputModeLabel = (mode: string) => {
    switch (mode.toLowerCase()) {
      case 'voice_edited':
        return 'Voice (Edited)';
      default:
        return mode;
    }
  };

  const renderCollapsedView = () => {
    if (!data) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No data available</div>;

    // Handle both dashboard data structure and API response structure
    const sourceBreakdown = data.sourceBreakdown || [];
    const inputModeBreakdown = data.inputModeBreakdown || [];
    const totalSessions = sourceBreakdown.reduce((sum, item) => sum + item.count, 0);

    return (
      <div style={{ height: '100%' }}>
        <div style={{ marginBottom: 16 }}>
          <MetricCardHeader
            title="Total Sessions"
            value={totalSessions}
            trend={data.source_shift_trend}
            subtitle="Across all sources"
          />
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {sourceBreakdown.slice(0, 3).map((source, index) => (
            <Col span={8} key={index}>
              <Statistic
                title={
                  source.source.toLowerCase() === 'web' || source.source.toLowerCase() === 'modal' || source.source.toLowerCase() === 'mobile' ? (
                    <span>
                      {source.source}
                      <AntTooltip 
                        title={source.source.toLowerCase() === 'web' 
                          ? "Sessions originating from web browsers (desktop and mobile web)"
                          : "Sessions originating from mobile applications or embedded modals"}
                        color="var(--neutral-90)"
                      >
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                      </AntTooltip>
                    </span>
                  ) : (
                    source.source
                  )
                }
                value={source.count}
                prefix={getSourceIcon(source.source)}
                valueStyle={{ color: getSourceColor(source.source) }}
                suffix={`(${source.percentage.toFixed(1)}%)`}
              />
            </Col>
          ))}
        </Row>

        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={sourceBreakdown}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                dataKey="count"
                label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
              >
                {sourceBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getSourceColor(entry.source)} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  value, 
                  props.payload.source
                ]} 
                labelFormatter={(label) => `${label} Sessions`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {inputModeBreakdown.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <h4 style={{ marginBottom: 10, fontSize: 14 }}>Input Mode Mix</h4>
            {inputModeBreakdown.map((mode, index) => (
              <div key={index} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span>
                    {getInputModeIcon(mode.mode)} {getInputModeLabel(mode.mode)}
                  </span>
                  <span>{mode.count} ({mode.percentage.toFixed(1)}%)</span>
                </div>
                <Progress
                  percent={mode.percentage}
                  strokeColor={getInputModeColor(mode.mode)}
                  showInfo={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderExpandedView = () => {
    return (
      <div style={{ height: '100%' }}>
        {/* Controls Row - Following Activity Trend Pattern */}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
            <Segmented
              value={aggregation}
              onChange={(value) => {
                setAggregation(value as AggregationType);
                // No need to reset date range for weekly/monthly since they don't use date selection
              }}
              options={[
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' },
              ]}
            />
            {aggregation === 'daily' ? (
              <>
                <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      // Check if range is more than 90 days
                      const daysDiff = dates[1].diff(dates[0], 'days');
                      if (daysDiff > 90) {
                        message.warning('Date range cannot exceed 90 days');
                        return;
                      }
                      setDateRange([dates[0], dates[1]]);
                    }
                  }}
                  format="YYYY-MM-DD"
                  style={{ width: 260 }}
                  disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                />
              </>
            ) : (
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
                Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
              </span>
            )}
          </Space>
          
          <Space>
            <Button type="primary" onClick={handleRefresh} loading={loading}>
              Apply Filters
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={!data}>
              Refresh
            </Button>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={() => {
                // TODO: Implement export functionality
                message.info('Export functionality coming soon');
              }}
            >
              Export
            </Button>
          </Space>
        </Space>

        {/* Chart */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" tip="Loading source distribution..." />
          </div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view source distribution</p>
            <p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
          </div>
        ) : (
          <>
            {/* Data content goes here */}
            {(() => {
              const { sourceBreakdown, sourceTrends, conversionRates, inputModeBreakdown = [] } = data;
              const totalSessions = sourceBreakdown.reduce((sum, item) => sum + item.count, 0);
              return (
                <>
                  {/* Summary Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {sourceBreakdown.map((source, index) => (
            <Col span={8} key={index}>
              <Statistic
                title={
                  source.source.toLowerCase() === 'web' || source.source.toLowerCase() === 'modal' || source.source.toLowerCase() === 'mobile' ? (
                    <span>
                      {source.source}
                      <AntTooltip 
                        title={source.source.toLowerCase() === 'web' 
                          ? "Sessions originating from web browsers (desktop and mobile web)"
                          : "Sessions originating from mobile applications or embedded modals"}
                        color="var(--neutral-90)"
                      >
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                      </AntTooltip>
                    </span>
                  ) : (
                    source.source
                  )
                }
                value={source.count}
                prefix={getSourceIcon(source.source)}
                valueStyle={{ color: getSourceColor(source.source) }}
                suffix={`(${source.percentage.toFixed(1)}%)`}
              />
            </Col>
          ))}
        </Row>

        {/* Source Distribution */}
        <Card title="Traffic Source Distribution" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      dataKey="count"
                      label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                    >
                      {sourceBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getSourceColor(entry.source)} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Sessions']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ padding: '20px 0' }}>
                <h4>Source Breakdown</h4>
                {sourceBreakdown.map((source, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>
                        {getSourceIcon(source.source)} {source.source}
                      </span>
                      <span>{source.count} ({source.percentage.toFixed(1)}%)</span>
                    </div>
                    <Progress 
                      percent={source.percentage} 
                      strokeColor={getSourceColor(source.source)} 
                      showInfo={false}
                    />
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Source Trends */}
        {sourceTrends && sourceTrends.length > 0 && (
          <Card title="Source Trends Over Time" style={{ marginBottom: 24 }}>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sourceTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => formatXAxisLabel(value, aggregation)}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label) => formatTooltipLabel(label, aggregation)}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="web" 
                    stroke="#1890ff" 
                    strokeWidth={2}
                    dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                    name="Web"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="modal" 
                    stroke="#52c41a" 
                    strokeWidth={2}
                    dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                    name="Modal"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="others" 
                    stroke="#faad14" 
                    strokeWidth={2}
                    dot={{ fill: '#faad14', strokeWidth: 2, r: 4 }}
                    name="Others"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {inputModeBreakdown.length > 0 && (
          <Card title="Input Mode Distribution" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={inputModeBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        dataKey="count"
                        label={({ mode, percentage }) => `${getInputModeLabel(mode)}: ${percentage.toFixed(1)}%`}
                      >
                        {inputModeBreakdown.map((entry, index) => (
                          <Cell key={`input-mode-cell-${index}`} fill={getInputModeColor(entry.mode)} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Messages']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: '20px 0' }}>
                  <h4>Input Modes</h4>
                  {inputModeBreakdown.map((mode, index) => (
                    <div key={index} style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span>
                          {getInputModeIcon(mode.mode)} {getInputModeLabel(mode.mode)}
                        </span>
                        <span>{mode.count} ({mode.percentage.toFixed(1)}%)</span>
                      </div>
                      <Progress
                        percent={mode.percentage}
                        strokeColor={getInputModeColor(mode.mode)}
                        showInfo={false}
                      />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {/* Conversion Rates */}
        {conversionRates && conversionRates.length > 0 && (
          <Card title="Conversion Rates by Source">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={conversionRates}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="source" />
                      <YAxis 
                        label={{ value: 'Conversion Rate (%)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value}%`, 'Conversion Rate']}
                        labelFormatter={(label) => `Source: ${label}`}
                      />
                      <Bar dataKey="conversionRate" fill="#1890ff" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: '20px 0' }}>
                  <h4>Conversion Performance</h4>
                  {conversionRates.map((source, index) => (
                    <div key={index} style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span>
                          {getSourceIcon(source.source)} {source.source}
                        </span>
                        <span>{source.conversionRate.toFixed(1)}%</span>
                      </div>
                      <Progress 
                        percent={source.conversionRate} 
                        strokeColor={getSourceColor(source.source)} 
                        showInfo={false}
                      />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {/* Source Summary */}
        {/* <Card title="Source Performance Summary" style={{ marginTop: 24 }}>
          <Row gutter={[16, 16]}>
            {sourceBreakdown.map((source, index) => (
              <Col span={8} key={index}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: 24, color: getSourceColor(source.source), marginBottom: 8 }}>
                    {getSourceIcon(source.source)}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: '#222', marginBottom: 4 }}>
                    {source.count}
                  </div>
                  <div style={{ color: '#666' }}>{source.source} Sessions</div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    {source.percentage.toFixed(1)}% of total traffic
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card> */}
                </>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%' }}>
      {isExpanded ? renderExpandedView() : renderCollapsedView()}
    </div>
  );
});

SourceDistribution.displayName = 'SourceDistribution';

export default SourceDistribution;
